import { faArrowRightToArc, faArrowLeftToArc } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { createColumnHelper } from '@tanstack/react-table';
import { useIntl } from 'react-intl';

import {
    GATEWAY,
    type ParkingGateways,
} from 'src/features/parkingManagement/api/ParkingGatewayAPIs/useParkingGateways';
import { CellEntryExitPointsActions } from 'src/features/parkingManagement/components/EntryExitGateways/CellEntryExitPointsActions';
import { parkingManagementMessages } from 'src/features/parkingManagement/utils/messages';
import { DataTableColumnGenericHeaderDefinition, DataTableColumnHeader } from 'src/registry/components/table';
import { Badge } from 'src/registry/ui/Badge';
import { generalMessages } from 'src/utils/messages';

const columnHelper = createColumnHelper<ParkingGateways>();

export const EntryExitPointsTableColumns = ({
    openEditParkingGatewayModal,
    openDeleteParkingGatewayModal,
}: {
    openEditParkingGatewayModal: (point: ParkingGateways) => void;
    openDeleteParkingGatewayModal: (point: ParkingGateways) => void;
}) => {
    const { formatMessage } = useIntl();

    return [
        columnHelper.accessor('name', {
            header: DataTableColumnHeader,
            meta: {
                title: formatMessage(generalMessages.name),
            },
        }),
        columnHelper.accessor('gateway_type', {
            header: DataTableColumnHeader,
            cell: ({ row }) => {
                const isEntry = row.original.gateway_type === GATEWAY.ENTRY;
                const badgeTheme = isEntry ? 'emerald' : 'red';
                const badgeText = isEntry
                    ? formatMessage(parkingManagementMessages.enter)
                    : formatMessage(parkingManagementMessages.exit);
                const badgeIcon = isEntry ? faArrowRightToArc : faArrowLeftToArc;

                return (
                    <Badge theme={badgeTheme}>
                        <FontAwesomeIcon icon={badgeIcon} />
                        {badgeText}
                    </Badge>
                );
            },
            enableHiding: true,
            meta: {
                title: formatMessage(parkingManagementMessages.type),
            },
        }),
        columnHelper.accessor('address', {
            header: DataTableColumnHeader,
            meta: {
                title: formatMessage(generalMessages.address),
            },
        }),
        columnHelper.accessor('coordinates', {
            header: DataTableColumnHeader,
            cell: ({ row }) => {
                return (
                    <div>
                        <div className="whitespace-nowrap">{`X ${row.original.coordinates?.coordinates[0]}`}</div>
                        <div className="whitespace-nowrap">{`Y ${row.original.coordinates?.coordinates[1]}`}</div>
                    </div>
                );
            },
            meta: {
                title: formatMessage(parkingManagementMessages.coordinates),
            },
        }),
        columnHelper.accessor('max_height_cm', {
            header: DataTableColumnHeader,
            cell: ({ getValue }) => {
                const heightCm = getValue();
                const heightInMeters = heightCm ? (heightCm / 100).toFixed(2) : '';
                const height = heightCm
                    ? `${heightInMeters}${formatMessage(parkingManagementMessages.metersAbbreviation)}`
                    : '';
                return <div>{height}</div>;
            },
            meta: {
                title: formatMessage(parkingManagementMessages.maxHeight),
            },
        }),
        columnHelper.accessor('max_width_cm', {
            header: DataTableColumnHeader,
            cell: ({ getValue }) => {
                const widthCm = getValue();
                const widthInMeters = widthCm ? (widthCm / 100).toFixed(2) : '';
                const width = widthCm
                    ? `${widthInMeters}${formatMessage(parkingManagementMessages.metersAbbreviation)}`
                    : '';
                return <div>{width}</div>;
            },
            enableHiding: true,
            meta: {
                title: formatMessage(parkingManagementMessages.maxWidth),
            },
        }),
        columnHelper.display({
            header: DataTableColumnGenericHeaderDefinition,
            id: 'actions',
            cell: (props) => (
                <CellEntryExitPointsActions
                    deleteCallback={openDeleteParkingGatewayModal}
                    editCallback={openEditParkingGatewayModal}
                    {...props}
                />
            ),
        }),
    ];
};
